/**
 * Feature Flags Context
 *
 * Provides feature flags from Hasura with fallback to local constants.
 * Supports real-time updates via GraphQL subscription (when available).
 */
import { FeatureFlagName } from "constants/featureFlags";
import { useGetFeatureFlagsQuery } from "graphql/generated";
import { ReactNode, createContext, useMemo } from "react";

/** Feature flag data from Hasura */
export interface FeatureFlagData {
    id: string;
    name: string;
    enabled: boolean;
    rules: any | null;
    category?: string | null;
}

/** Feature flags context value */
interface FeatureFlagsContextValue {
    /** Feature flags map (name -> data) */
    flags: Map<string, FeatureFlagData>;
    /** Loading state */
    loading: boolean;
    /** Error state */
    error: Error | null;
    /** Whether flags are from Hasura (true) or constants (false) */
    isFromHasura: boolean;
    /** Refetch flags from Hasura */
    refetch: () => void;
}

export const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
    flags: new Map(),
    loading: false,
    error: null,
    isFromHasura: false,
    refetch: () => {}
});

interface FeatureFlagsProviderProps {
    children: ReactNode;
    /**
     * Whether to fetch from Hasura
     * Set to false to always use local constants (useful for Chrome extension static build)
     */
    enableHasura?: boolean;
}

/**
 * Feature Flags Provider
 *
 * Fetches feature flags from Hasura and provides them to the app.
 * Falls back to local constants if Hasura is unavailable.
 *
 * @example
 * ```tsx
 * // In _app.tsx
 * <FeatureFlagsProvider enableHasura={process.env.BUILD_TARGET !== "extension"}>
 *   <Component {...pageProps} />
 * </FeatureFlagsProvider>
 * ```
 */
export function FeatureFlagsProvider({
    children,
    enableHasura = true
}: FeatureFlagsProviderProps) {
    // Fetch from Hasura using generated hook
    const {
        data: hasuraData,
        loading: hasuraLoading,
        error: hasuraError,
        refetch
    } = useGetFeatureFlagsQuery({
        skip: !enableHasura,
        fetchPolicy: "network-only" // Always fetch fresh data to avoid stale cache
    });

    // Transform Hasura data to Map format
    const flags = useMemo(() => {
        if (hasuraData?.mix_feature_flag_active) {
            const hasuraFlags = new Map<string, FeatureFlagData>();
            hasuraData.mix_feature_flag_active.forEach((flag) => {
                if (flag.name) {
                    hasuraFlags.set(flag.name, {
                        id: flag.id || "",
                        name: flag.name,
                        enabled: flag.enabled || false,
                        rules: flag.rules,
                        category: flag.category
                    });
                }
            });
            return hasuraFlags;
        }
        // Only use Hasura data - no fallback to local constants
        return new Map<string, FeatureFlagData>();
    }, [hasuraData]);

    const value: FeatureFlagsContextValue = {
        flags,
        loading: hasuraLoading,
        error: hasuraError || null,
        isFromHasura: !!hasuraData?.mix_feature_flag_active,
        refetch: () => {
            refetch();
        }
    };

    return (
        <FeatureFlagsContext.Provider value={value}>
            {children}
        </FeatureFlagsContext.Provider>
    );
}
